// Anfragen (prototype, spec 3.4): graded inquiries with check list, key facts, batch mass and the draft that fits
// the grade; convert into a deal, change the grade (teaches the AI), ignore; release the Rückfrage or Absage, one by
// one or several at once (spec 8). It goes from sales@ only while sending is switched on.
import type { Inquiry } from "../api";
import { loadAll, render, S, write } from "../state";
import { $, emptyState, esc, fmtDT, fmtKg, num, toast } from "../util";
import { mailCard } from "./mailcard";
import { showOriginal } from "./original";
import { taskBox } from "./tasks";
import { markenHtml } from "./volumen";

const SRC: Record<string, string> = { info: "info@", sales: "sales@", frank: "Postfach F. Elvers", hubspot: "HubSpot", website: "Website-Formular", pipeline: "Weiterleitung" };
const GRADE_TXT: Record<Inquiry["grade"], string> = { A: "Deal-Vorschlag", B: "Rückfragen nötig", C: "Absage" };
const STATUS_TXT: Record<string, string> = { deal: "Deal angelegt", rueckfrage: "Rückfrage freigegeben", absage: "Absage freigegeben", ignoriert: "Ignoriert" };
const SENDING_OFF = "Der Versand ist noch nicht freigeschaltet.";
const TEST_MODE = "Testmodus: Die Mail geht nur an die interne Testadresse, nicht an den Kunden.";
const canSend = () => !S.readOnly && S.mailActions.send !== "off";
const kg = (v: number) => `${v.toLocaleString("de-DE", { maximumFractionDigits: 0 })} kg`;

function checkIcon(ok: boolean | null): string {
  return ok === true
    ? `<span class="ck yes" aria-label="erfüllt">✓</span>`
    : ok === false
      ? `<span class="ck no" aria-label="nicht erfüllt">✗</span>`
      : `<span class="ck unk" aria-label="offen">?</span>`;
}

function massBlock(f: Inquiry["facts"]): string {
  const min = S.meta?.rules.minBatchKg ?? 50;
  if (!num(f.mass_kg)) {
    return `<div><div class="mass">Chargenmasse: <b>unbekannt</b>, Menge fehlt</div><div class="meter" aria-hidden="true"><span class="min" style="left:${((min / 150) * 100).toFixed(1)}%"></span></div><div class="mass muted">Mindestmenge ${kg(min)}</div></div>`;
  }
  const max = Math.max(150, f.mass_kg * 1.15);
  const ok = f.mass_kg >= min;
  return `<div><div class="mass">Chargenmasse ≈ <b>${kg(f.mass_kg)}</b>${f.mass_calc ? ` <span class="muted">(${esc(f.mass_calc)})</span>` : ""}${f.vol_source === "schaetzung" ? ` <span class="muted">· Schätzung</span>` : ""}</div>
    <div class="meter" role="img" aria-label="${kg(f.mass_kg)} von mindestens ${kg(min)}"><i style="width:${Math.min(100, (f.mass_kg / max) * 100).toFixed(1)}%;background:var(${ok ? "--ok" : "--crit"})"></i><span class="min" style="left:${((min / max) * 100).toFixed(1)}%"></span></div>
    <div class="mass muted">Mindestmenge ${kg(min)}</div>
    ${num(f.kg_per_year) ? `<div class="mass" style="margin-top:6px">Pro Jahr ≈ <b>${fmtKg(f.kg_per_year)}</b></div>` : ""}</div>`;
}

function inqCard(i: Inquiry): string {
  const f = i.facts ?? {};
  const ro = S.readOnly;
  const open = i.status === "neu";
  const regrade = `<select data-regrade aria-label="Stufe ändern" ${ro ? "disabled" : ""}><option value="">Stufe ändern …</option>${(["A", "B", "C"] as const)
    .filter((g) => g !== i.grade)
    .map((g) => `<option value="${g}">${g}: ${GRADE_TXT[g]}</option>`)
    .join("")}<option value="X">Kein Lead (entfernen)</option></select>`;
  const original = i.hasOriginal && i.mailId ? `<button class="btn" data-original="${esc(i.mailId)}">Original</button>` : "";
  let actions: string;
  if (open && i.grade === "A") {
    actions = `<div class="actions"><button class="btn primary" data-convert ${ro ? "disabled" : ""}>Deal anlegen</button>${regrade}<button class="btn" data-ignore ${ro ? "disabled" : ""}>Ignorieren</button>${original}</div>`;
  } else if (open) {
    const kind = i.grade === "C" ? "absage" : "rueckfrage";
    const draft = i.drafts[kind] ?? "";
    const send = canSend();
    const title = S.mailActions.send === "test" ? TEST_MODE : send ? "Geht von sales@ an den Absender." : SENDING_OFF;
    actions = `<div class="draft"><label for="draft-${esc(i.id)}">${kind === "absage" ? "Entwurf Absage" : "Entwurf Rückfrage"} an ${esc(i.fromName || i.fromAddr)}, wird von sales@ gesendet</label>
        <textarea id="draft-${esc(i.id)}" data-draft="${kind}" ${ro ? "disabled" : ""}>${esc(draft)}</textarea></div>
      <div class="actions"><button class="btn primary" data-release="${kind}" ${send ? "" : "disabled"} title="${esc(title)}">${kind === "absage" ? "Absage freigeben" : "Rückfrage freigeben"}</button>
        ${send ? `<label class="sel pick"><input type="checkbox" data-pick ${S.inqPicked.has(i.id) ? "checked" : ""}> Sammelfreigabe</label>` : ""}
        ${i.grade === "B" ? `<button class="btn" data-convert ${ro ? "disabled" : ""}>Trotzdem Deal anlegen</button>` : ""}${regrade}<button class="btn" data-ignore ${ro ? "disabled" : ""}>Ignorieren</button>${original}</div>`;
  } else {
    actions = `<div class="statusline"><span class="pill ${i.status === "deal" ? "p-ok" : "p-info"}">${esc(STATUS_TXT[i.status] ?? i.status)}</span>${i.dealId ? ` <a href="#deal-${esc(i.dealId)}">Deal öffnen</a>` : ""}${i.status === "ignoriert" && !ro ? ` <button class="link" data-reopen>Wieder öffnen</button>` : ""}</div>`;
  }
  return `<article class="inq ${open ? "" : "done"}" data-inq="${esc(i.id)}">
    <div class="inq-main">
      <div class="inq-top"><span class="grade g-${esc(i.grade)}" title="Stufe ${esc(i.grade)}: ${esc(GRADE_TXT[i.grade])}">${esc(i.grade)}</span>
        <div class="who"><b>${esc(i.company || "Ohne Firmenname")}</b>${markenHtml({ pharma: i.pharma ?? false, jahresvolumen: i.jahresvolumen })}<span>${esc(i.fromName || "")} · ${esc(i.fromAddr)}${i.freemail ? " · Freemail" : ""}${i.existingCompany ? " · bekannte Firma" : ""} · ${esc(SRC[i.source] ?? i.source)} · <span class="mono">${fmtDT(i.receivedAt)}</span>${i.mailCount > 1 ? ` · ${i.mailCount} Mails` : ""}</span></div></div>
      <div class="subj">${esc(i.subject ?? "")}</div>
      ${i.excerpt ? `<p class="excerpt">${esc(i.excerpt)}</p>` : ""}
      <p class="why"><b>${esc(GRADE_TXT[i.grade])}:</b> ${esc(i.gradeReason ?? "")}${i.gradedBy === "manual" ? ` <span class="pill p-acc">manuell umgestuft</span>` : ""}</p>
      ${actions}
    </div>
    <div class="inq-side">
      <span class="eyebrow">Eckdaten</span>
      <dl class="facts">
        <dt>Produkt</dt><dd>${esc(f.product || "–")}</dd>
        <dt>Form</dt><dd>${esc(f.form || "–")}</dd>
        <dt>Packmittel</dt><dd>${esc(f.pack || "–")}</dd>
        <dt>Menge</dt><dd>${esc(f.quantity || "–")}</dd>
        ${i.website ? `<dt>Website</dt><dd class="mono" style="font-size:12px">${esc(i.website)}</dd>` : ""}
      </dl>
      ${massBlock(f)}
      <span class="eyebrow">Prüfung</span>
      <ul class="checks">${i.checks.map((c) => `<li>${checkIcon(c.ok)}<span>${esc(c.label)}${c.note ? ` <small>${esc(c.note)}</small>` : ""}</span></li>`).join("")}</ul>
      ${open || i.tasks.length ? `<span class="eyebrow">Aufgaben</span>${taskBox({ inquiryId: i.id }, i.tasks, { empty: "Keine." })}` : ""}
    </div>
  </article>`;
}

export function renderAnfragen(): void {
  const el = $("#v-anfragen");
  if (!S.loaded) {
    el.innerHTML = emptyState("Lade Anfragen …", "");
    return;
  }
  // Keep a draft or a task that is being edited.
  if (el.contains(document.activeElement) && (document.activeElement as HTMLElement).matches("textarea[data-draft], .taskform input, .tk-ctl input")) return;
  const r = S.meta?.rules;
  const open = S.inquiries.filter((i) => i.status === "neu");
  const rank = { A: 0, B: 1, C: 2 };
  const list = (S.inqDone ? S.inquiries : open)
    .filter((i) => S.inqGrade === "alle" || i.grade === S.inqGrade)
    .sort((a, b) => (a.status === "neu" ? 0 : 1) - (b.status === "neu" ? 0 : 1) || rank[a.grade] - rank[b.grade] || b.receivedAt.localeCompare(a.receivedAt));
  const cnt = (g: Inquiry["grade"]) => open.filter((i) => i.grade === g).length;
  const intro = `<div class="brief-head"><span class="eyebrow">Anfragen</span><h1>Neue Anfragen aus allen Postfächern</h1>
    <p>Die KI bewertet jede neue Anfrage nach euren Regeln, mit Prüfliste, Eckdaten und einem Entwurf für Rückfrage oder Absage. Antworten gehen erst nach eurer Freigabe raus.</p></div>
    ${r ? `<div class="rules"><span><b>Mindestmenge</b> ${kg(r.minBatchKg)} je Charge</span><span><b>Nur Solida</b> ${esc(r.solids.join(", "))}</span><span><b>Keine Liquida</b> ${esc(r.liquids.join(", "))}</span><span><b>No-Go</b> ${esc(r.noGo.join(", ") || "–")}</span></div>` : ""}`;
  const seg = (["alle", "A", "B", "C"] as const)
    .map((g) => `<button type="button" data-gfilter="${g}" aria-pressed="${S.inqGrade === g}">${g === "alle" ? "Alle" : g} <span class="mono">${g === "alle" ? open.length : cnt(g)}</span></button>`)
    .join("");
  const filters = `<div class="filters"><div class="seg" role="group" aria-label="Stufe">${seg}</div>
    <label class="sel"><input type="checkbox" id="inq-done" ${S.inqDone ? "checked" : ""}> Erledigte zeigen</label></div>`;
  const picked = open.filter((i) => S.inqPicked.has(i.id));
  const bulk = canSend()
    ? `${S.mailActions.send === "test" ? `<p class="note warn">${esc(TEST_MODE)}</p>` : ""}${
        picked.length ? `<div class="bulkbar"><span><b>${picked.length}</b> zur Sammelfreigabe ausgewählt</span><button class="btn primary" data-release-all>Alle ${picked.length} freigeben</button><button class="btn" data-pick-none>Auswahl aufheben</button></div>` : ""
      }`
    : "";
  const cards = list.length
    ? `<div class="inqs">${list.map(inqCard).join("")}</div>`
    : emptyState(open.length ? "Keine Anfragen in dieser Stufe" : "Keine offenen Anfragen", "Neue Anfragen aus den Postfächern erscheinen hier mit Bewertung.");
  const pending = S.pendingInquiries.length
    ? `<div class="sec"><h3>Noch nicht bewertet <small>${S.pendingInquiries.length}</small></h3><div class="klaer">${S.pendingInquiries.map((m) => mailCard(m, { allowAnfrage: false, allowUnklar: true })).join("")}</div></div>`
    : "";
  el.innerHTML = intro + filters + bulk + cards + pending;
}

/** The draft as it stands in the card (maybe edited, not yet saved). */
function textOf(i: Inquiry, kind: "rueckfrage" | "absage"): string {
  const field = document.querySelector<HTMLTextAreaElement>(`#v-anfragen [data-inq="${CSS.escape(i.id)}"] textarea[data-draft]`);
  return (field?.value ?? i.drafts[kind] ?? "").trim();
}

/** Bulk release: each inquiry its own mail and audit entry; the ones that failed stay picked. */
async function releasePicked(): Promise<void> {
  const items = S.inquiries
    .filter((i) => i.status === "neu" && S.inqPicked.has(i.id))
    .map((i) => {
      const kind = i.grade === "C" ? ("absage" as const) : ("rueckfrage" as const);
      return { id: i.id, version: i.version, kind, body: textOf(i, kind) };
    })
    .filter((x) => x.body);
  if (!items.length) return toast("Keine freigebbaren Entwürfe ausgewählt.");
  let failed = 0;
  const ok = await write(async () => {
    const results = await S.api.releaseInquiries(items);
    for (const r of results) if (!r.error) S.inqPicked.delete(r.id);
    failed = results.filter((r) => r.error).length;
  });
  // The request failed as a whole: its error message stays, nothing was released.
  if (!ok) return;
  toast(failed ? `${items.length - failed} freigegeben, ${failed} nicht (inzwischen geändert). Bitte prüfen.` : `${items.length} Antworten freigegeben.`);
}

/** Draft saves in flight per inquiry: an action on the same inquiry waits for them. */
const saving = new Map<string, Promise<boolean>>();

const inqOf = (el: Element) => {
  const id = el.closest<HTMLElement>("[data-inq]")?.dataset.inq;
  return id ? S.inquiries.find((i) => i.id === id) : undefined;
};

export function bindAnfragen(): void {
  document.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(
      "#v-anfragen [data-gfilter], #v-anfragen [data-convert], #v-anfragen [data-ignore], #v-anfragen [data-reopen], #v-anfragen [data-original], #v-anfragen [data-release], #v-anfragen [data-release-all], #v-anfragen [data-pick-none]",
    );
    if (!t) return;
    if (t.dataset.pickNone !== undefined) {
      S.inqPicked.clear();
      return renderAnfragen();
    }
    if (t.dataset.releaseAll !== undefined) return void releasePicked();
    if (t.dataset.gfilter) {
      S.inqGrade = t.dataset.gfilter as typeof S.inqGrade;
      return renderAnfragen();
    }
    if (t.dataset.original) return void showOriginal(t.dataset.original);
    let i = inqOf(t);
    if (!i) return;
    // Leaving the draft saved it (a new version): the action waits for that and uses the new version.
    const pending = saving.get(i.id);
    if (pending) {
      await pending;
      i = inqOf(t) ?? S.inquiries.find((x) => x.id === i!.id);
      if (!i) return;
    }
    if (t.dataset.convert !== undefined) {
      let dealId: string | null = null;
      const ok = await write(async () => {
        dealId = (await S.api.convertInquiry(i.id)).dealId;
      }, "Deal angelegt. Kontakt, Eckdaten und Mails sind übernommen.");
      if (ok && dealId) location.hash = `deal-${dealId}`;
      return;
    }
    if (t.dataset.release) {
      const kind = t.dataset.release as "rueckfrage" | "absage";
      const text = textOf(i, kind);
      if (!text) return toast("Der Entwurf ist leer.");
      S.inqPicked.delete(i.id);
      return void write(
        () => S.api.releaseInquiry(i.id, i.version, { kind, body: text }),
        `${kind === "absage" ? "Absage" : "Rückfrage"} freigegeben. Sie geht von sales@ ${S.mailActions.send === "test" ? "an die Testadresse" : "raus"}.`,
      );
    }
    if (t.dataset.ignore !== undefined) return void write(() => S.api.updateInquiry(i.id, i.version, { status: "ignoriert" }), "Ignoriert. Unter „Erledigte zeigen“ wieder zu finden.");
    if (t.dataset.reopen !== undefined) return void write(() => S.api.updateInquiry(i.id, i.version, { status: "neu" }), "Wieder geöffnet");
  });
  document.addEventListener("change", (e) => {
    const t = e.target as HTMLElement;
    if (t.matches("#v-anfragen input[data-pick]")) {
      const i = inqOf(t);
      if (!i) return;
      if ((t as HTMLInputElement).checked) S.inqPicked.add(i.id);
      else S.inqPicked.delete(i.id);
      return renderAnfragen();
    }
    if (t.id === "inq-done") {
      S.inqDone = (t as HTMLInputElement).checked;
      return void loadAll().then(render, () => undefined);
    }
    if (!t.matches("#v-anfragen select[data-regrade]")) return;
    const sel = t as HTMLSelectElement;
    const i = inqOf(sel);
    if (!i || !sel.value) return;
    const g = sel.value;
    void write(
      () => S.api.updateInquiry(i.id, i.version, { grade: g }),
      g === "X" ? "Als „kein Lead“ entfernt. Die Mail bleibt im Postfach." : `Auf Stufe ${g} gesetzt. Die Korrektur fließt als Beispiel in die Bewertung ein.`,
    );
  });
  // Drafts are saved when the field is left.
  document.addEventListener(
    "blur",
    (e) => {
      const t = e.target as HTMLTextAreaElement;
      if (!t.matches?.("#v-anfragen textarea[data-draft]")) return;
      const i = inqOf(t);
      if (!i) return;
      const kind = t.dataset.draft as "rueckfrage" | "absage";
      if ((i.drafts[kind] ?? "") === t.value) return;
      const save = write(() => S.api.updateInquiry(i.id, i.version, kind === "absage" ? { draftAbsage: t.value } : { draftRueckfrage: t.value }), "Entwurf gespeichert");
      saving.set(i.id, save);
      void save.finally(() => {
        if (saving.get(i.id) === save) saving.delete(i.id);
      });
    },
    true,
  );
}
